import { Shield, Users, BarChart, Briefcase, Building2, Palette, Newspaper, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const AdminHero = () => {
  const navigate = useNavigate();

  const targetAudiences = [
    { icon: Briefcase, label: "Artist Management", desc: "Talent agencies & managers" },
    { icon: Building2, label: "Venue Partners", desc: "Clubs, lounges & event spaces" },
    { icon: Palette, label: "Interior Design", desc: "Creative space curators" },
    { icon: Newspaper, label: "Media & PR", desc: "News providers & publishers" },
    { icon: Video, label: "Production Teams", desc: "Content & event production" },
  ];

  return (
    <section className="min-h-screen bg-black flex items-center relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-pink-500/5 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>
      
      <div className="container mx-auto px-6 py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center bg-purple-500/10 backdrop-blur-sm border border-purple-500/20 rounded-full px-4 py-2">
                <Shield className="w-4 h-4 text-purple-400 mr-2" />
                <span className="text-sm text-purple-300">For Marketing Agencies & Industry Partners</span>
              </div>
              
              <h1 className="text-5xl lg:text-7xl font-bold text-white leading-tight">
                Power Your
                <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"> Agency </span>
                With Africa's Creative Network
              </h1>
              
              <p className="text-xl text-gray-300 max-w-2xl">
                The ultimate platform for marketing agencies managing artists, venues, production teams, 
                and creative spaces. Streamline bookings, amplify reach, and connect with Africa's 
                entertainment ecosystem.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button 
                onClick={() => navigate("/agency-pricing")}
                className="bg-white text-black hover:bg-gray-200 px-6 py-3 rounded-xl font-semibold"
              >
                Request Demo
              </Button>
              <Button 
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 px-6 py-3 rounded-xl"
              >
                View Case Studies
              </Button>
            </div>
            
            <div className="flex items-center space-x-8 text-sm text-gray-400">
              <div className="flex items-center">
                <Users className="w-4 h-4 mr-1 text-purple-400" />
                <span>200+ Agency Partners</span>
              </div>
              <div className="flex items-center">
                <BarChart className="w-4 h-4 mr-1 text-pink-400" />
                <span>R 45M+ Managed Revenue</span>
              </div>
            </div>
          </div>
          
          <div className="relative">
            <div className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-3xl p-8 space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-white mb-2">Who We Serve</h3>
                <p className="text-gray-400 text-sm">Empowering diverse creative industries</p>
              </div>
              
              <div className="space-y-3">
                {targetAudiences.map((audience, index) => (
                  <div 
                    key={index} 
                    className="bg-white/10 rounded-xl p-4 border border-white/10 hover:border-purple-500/50 transition-all duration-300 group cursor-pointer"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl flex items-center justify-center group-hover:from-purple-500/30 group-hover:to-pink-500/30 transition-all">
                        <audience.icon className="w-6 h-6 text-purple-400" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-white">{audience.label}</h4>
                        <p className="text-sm text-gray-400">{audience.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdminHero;
